/**
 * The enquiry form's vocabulary, shared by the client component and the server action.
 *
 * Kept apart from `enquiry-schema.ts` so the client bundle carries the option list and
 * the labels without carrying zod, and apart from `enquiry-action.ts` because a
 * `'use server'` module may export only async functions.
 */

/**
 * Who a visitor tells us they are. The values travel to the backend, which keeps the
 * same list in `api/routes/contact.py` under the WyseHub brand; add to both or the
 * backend rejects the new value. WyseAi's form asks about the kind of work; WyseHub's
 * asks about the enquirer, because who is asking decides who replies.
 */
export const WORK_KINDS = [
  'refer-claims',
  'service-claims',
  'reporting',
  'partnership',
  'question',
  'other',
] as const

export type WorkKind = (typeof WORK_KINDS)[number]

export const WORK_KIND_LABELS: Readonly<Record<WorkKind, string>> = {
  'refer-claims': 'An insurer or broker referring claims',
  'service-claims': 'A hire, repair or accident-management partner',
  reporting: 'Looking at reporting and oversight',
  partnership: 'Exploring a partnership',
  question: 'Asking a question',
  other: 'Something else',
}

export function isWorkKind(value: unknown): value is WorkKind {
  return (
    typeof value === 'string' &&
    (WORK_KINDS as readonly string[]).includes(value)
  )
}

/**
 * Where the form was filled in. Goes into the internal email so we can tell. Prefixed
 * because the backend serves both brands' forms from one endpoint and files by source.
 */
export const ENQUIRY_SOURCES = ['wysehub-homepage', 'wysehub-contact'] as const

export type EnquirySource = (typeof ENQUIRY_SOURCES)[number]

export function isEnquirySource(value: unknown): value is EnquirySource {
  return (
    typeof value === 'string' &&
    (ENQUIRY_SOURCES as readonly string[]).includes(value)
  )
}

/**
 * The honeypot's field name.
 *
 * Plausible enough that a form-filling bot completes it, and with no autofill heuristic
 * in any browser, so a person's password manager never fills it by mistake.
 */
export const HONEYPOT_FIELD = 'company_ref'
